import { FC, MouseEventHandler, ReactNode } from 'react'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import type { Theme } from '@mui/material/styles'
import { getNf, nf, type NfTokens } from '../nfTokens'

export interface NfPillProps {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  type?: 'button' | 'submit'
  onClick?: MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  loading?: boolean
  icon?: ReactNode
  'data-testid'?: string
}

const variantSx = (variant: NonNullable<NfPillProps['variant']>, t: NfTokens) => {
  if (variant === 'primary') {
    return {
      bgcolor: t.green,
      color: t.onGreen,
      border: '1px solid transparent',
      '&:hover': { bgcolor: t.darkGreenHover },
    }
  }
  if (variant === 'secondary') {
    return {
      bgcolor: t.surface,
      color: t.ink2,
      border: `1px solid ${t.line}`,
      '&:hover': { bgcolor: t.line2 },
    }
  }
  return {
    bgcolor: 'transparent',
    color: t.muted,
    border: '1px solid transparent',
    '&:hover': { bgcolor: t.line2 },
  }
}

/**
 * Bouton pilule reproduisant "NfPill" de la maquette : hauteur 36, rayon 99,
 * 3 variantes (primary/secondary/ghost). Toujours type="button" par défaut
 * (jamais "submit") : la modale déclenche l'envoi via formik.submitForm()
 * pour éviter qu'un bouton change de type juste après un clic (piège React
 * classique qui peut faire soumettre le formulaire malgré lui).
 */
export const NfPill: FC<NfPillProps> = ({
  children,
  variant = 'secondary',
  type = 'button',
  onClick,
  disabled,
  loading,
  icon,
  'data-testid': dataTestId,
}) => (
  <ButtonBase
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={dataTestId}
    sx={(theme: Theme) => {
      const t = getNf(theme)
      return {
        ...variantSx(variant, t),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        height: `${nf.fieldHeight}px`,
        px: 2.25,
        borderRadius: `${nf.radiusPill}px`,
        fontFamily: 'inherit',
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color .14s ease',
      }
    }}
  >
    {loading ? (
      <CircularProgress
        size={12}
        thickness={5}
        sx={(theme) => ({
          color: variant === 'primary' ? getNf(theme).onGreen : getNf(theme).muted,
        })}
      />
    ) : (
      icon
    )}
    {children}
  </ButtonBase>
)

export default NfPill
